import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import settingsService from '../../services/settingsService';
import scheduleService from '../../services/scheduleService';
import { ageGroupsService } from '../../services';
import ConfirmationModal from './ConfirmationModal';

const SettingsTailwind = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [deleteOptions, setDeleteOptions] = useState({
    results: true,
    schedule: true,
    participantGroups: true,
    participantCategories: true,
    groups: true,
    participants: true,
    teamData: true,
    categories: false,
    ageGroups: false,
    nations: false
  });
  const [ageGroups, setAgeGroups] = useState([]);
  const [demoOptions, setDemoOptions] = useState({
    totalParticipants: 200,
    genderRatio: { M: 50, W: 50 },
    ageGroupRatios: {}
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [confirmationType, setConfirmationType] = useState('update-times');
  // Zusätzlicher Bestätigungsdialog für Datenaktionen
  const [showActionConfirm, setShowActionConfirm] = useState(false);
  const [actionType, setActionType] = useState(null); // 'reset-data' | 'seed-demo'

  // Lade Settings beim Komponenten-Mount
  useEffect(() => {
    loadSettings();
    loadAgeGroups();
  }, []);
  const loadAgeGroups = async () => {
    try {
      const res = await ageGroupsService.getAllAgeGroups();
      if (res.success) {
        // "Alle" Altersgruppe ausschließen (nur für Team-Wettbewerb)
        const allAgeGroups = res.data || [];
        const validAgeGroups = allAgeGroups.filter(ag => ag.name !== 'Alle');
        
        // Finde "Alle" Altersgruppe ID, falls vorhanden
        const alleAgeGroup = allAgeGroups.find(ag => ag.name === 'Alle');
        
        setAgeGroups(validAgeGroups);
        
        // Entferne "Alle" aus den ageGroupRatios, falls vorhanden
        if (alleAgeGroup && demoOptions.ageGroupRatios && demoOptions.ageGroupRatios[alleAgeGroup.id] !== undefined) {
          const cleanedRatios = { ...demoOptions.ageGroupRatios };
          delete cleanedRatios[alleAgeGroup.id];
          setDemoOptions(prev => ({ ...prev, ageGroupRatios: cleanedRatios }));
        }
        
        // Default 20% pro Altersklasse, letzte korrigiert auf 100%
        if (!demoOptions.ageGroupRatios || Object.keys(demoOptions.ageGroupRatios).length === 0) {
          const ratios = {};
          let acc = 0;
          validAgeGroups.forEach((ag, idx) => {
            if (idx === validAgeGroups.length - 1) ratios[ag.id] = Math.max(0, 100 - acc);
            else { ratios[ag.id] = 20; acc += 20; }
          });
          setDemoOptions(prev => ({ ...prev, ageGroupRatios: ratios }));
        }
      }
    } catch (e) {
      // still silent
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await settingsService.getAllSettings();
      
      if (result.success) {
        setSettings(result.data);
        if (result.fallback) {
          addToast('info', 'Verwende Standard-Einstellungen (Backend nicht verfügbar)');
        }
      } else {
        setError(result.error || 'Fehler beim Laden der Einstellungen');
        addToast('error', result.error || 'Fehler beim Laden der Einstellungen');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Settings:', err);
      setError('Fehler beim Laden der Einstellungen');
      addToast('error', 'Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const settingsArray = settingsService.settingsToArray(settings);
      const result = await settingsService.updateMultipleSettings(settingsArray);
      
      if (result.success) {
        addToast('success', result.message || 'Einstellungen erfolgreich gespeichert');
        
        const hasEventsResult = await scheduleService.hasExistingEvents();
        if (hasEventsResult.success && hasEventsResult.data.hasEvents) {
          setConfirmationType('update-times');
          setShowConfirmation(true);
        }
        
        await loadSettings();
      } else {
        addToast('error', result.error || 'Fehler beim Speichern der Einstellungen');
      }
    } catch (err) {
      console.error('Fehler beim Speichern der Settings:', err);
      addToast('error', 'Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    // Nutzt bestehenden kleinen Confirm-Dialog; könnte bei Bedarf auch in das Modal migriert werden
    if (!window.confirm('Möchten Sie wirklich alle Einstellungen auf Standardwerte zurücksetzen?')) return;
    try {
      setSaving(true);
      setError(null);

      const result = await settingsService.resetSettings();
      
      if (result.success) {
        addToast('success', result.message || 'Einstellungen erfolgreich zurückgesetzt');
        await loadSettings();
      } else {
        addToast('error', result.error || 'Fehler beim Zurücksetzen der Einstellungen');
      }
    } catch (err) {
      console.error('Fehler beim Zurücksetzen der Settings:', err);
      addToast('error', 'Fehler beim Zurücksetzen der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const handleResetData = () => {
    setActionType('reset-data');
    setShowActionConfirm(true);
  };

  const performResetData = async () => {
    try {
      setSaving(true);
      const res = await settingsService.resetAllData(deleteOptions);
      if (res.success) {
        addToast('success', res.message || 'Daten erfolgreich zurückgesetzt');
      } else {
        addToast('error', res.error || 'Fehler beim Zurücksetzen der Daten');
      }
    } catch (e) {
      addToast('error', 'Fehler beim Zurücksetzen der Daten');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedDemo = () => {
    setActionType('seed-demo');
    setShowActionConfirm(true);
  };

  const performSeedDemo = async () => {
    try {
      setSaving(true);
      const res = await settingsService.seedDemoData({
        totalParticipants: Number(demoOptions.totalParticipants) || 200,
        genderRatio: demoOptions.genderRatio,
        ageGroupRatios: demoOptions.ageGroupRatios
      });
      if (res.success) {
        addToast('success', res.message || 'Demo-Daten erfolgreich generiert');
      } else {
        addToast('error', res.error || 'Fehler beim Generieren der Demo-Daten');
      }
    } catch (e) {
      addToast('error', 'Fehler beim Generieren der Demo-Daten');
    } finally {
      setSaving(false);
    }
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  const handleConfirmGenerate = async () => {
    setIsGenerating(true);
    setShowConfirmation(false);
    
    try {
      let scheduleResult;
      
      if (confirmationType === 'update-times') {
        addToast('info', 'Zeiten im Zeitplan werden aktualisiert...');
        scheduleResult = await scheduleService.updateScheduleTimes();
        if (scheduleResult.success) {
          addToast('success', 'Zeiten im Zeitplan erfolgreich basierend auf neuen Einstellungen aktualisiert');
        } else {
          addToast('error', 'Fehler beim Aktualisieren der Zeiten: ' + scheduleResult.error);
        }
      } else {
        addToast('info', 'Zeitplan wird neu erstellt...');
        scheduleResult = await scheduleService.generateSchedule();
        if (scheduleResult.success) {
          addToast('success', 'Zeitplan erfolgreich basierend auf neuen Einstellungen neu erstellt');
        } else {
          addToast('error', 'Fehler beim Neu-Erstellen des Zeitplans: ' + scheduleResult.error);
        }
      }
    } catch (error) {
      const action = confirmationType === 'update-times' ? 'Aktualisieren der Zeiten' : 'Neu-Erstellen des Zeitplans';
      addToast('error', `Fehler beim ${action}`);
      console.error(`Fehler beim ${action}:`, error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  const searchParams = new URLSearchParams(location.search);
  const activeView = searchParams.get('view'); // 'org' | 'user' | null

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Settings Cards Skeleton */}
          <div className="space-y-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-48 mb-4"></div>
                <div className="space-y-4">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="space-y-2">
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                      <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-900 dark:text-slate-100 font-medium mb-4">{error}</p>
                <button 
                  onClick={loadSettings} 
                  className="inline-flex items-center px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-lg hover:bg-primary-dark dark:hover:bg-primary transition-colors"
                >
                  <i className="fas fa-redo mr-2"></i>
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t("admin.settings.title", "Einstellungen")}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                {t("admin.settings.subtitle", "Konfigurieren Sie die Zeitplan-Parameter für den Wettkampf.")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{Object.keys(settings).length}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{t("admin.settings.settings", "Einstellungen")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t("admin.settings.search", "Einstellungen suchen...")}
                className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:bg-slate-50 dark:disabled:bg-slate-800 disabled:text-slate-400 dark:disabled:text-slate-500"
                disabled
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
            </div>

            <div className="flex items-center gap-3">
              <button 
                className="inline-flex items-center px-4 h-[46px] bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={handleSave}
                disabled={saving}
              >
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'} mr-2`}></i>
                {saving ? "Speichere..." : t("admin.settings.save", "Einstellungen speichern")}
              </button>
              <button 
                className="inline-flex items-center px-4 h-[46px] bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={handleReset}
                disabled={saving}
              >
                <i className="fas fa-undo mr-2"></i>
                {saving ? "Setze zurück..." : t("admin.settings.reset", "Auf Standardwerte zurücksetzen")}
              </button>
            </div>
          </div>
        </div>

        {/* Organisation – Zeitplan-Konfiguration */}
        {activeView !== 'user' && (
        <section className="mb-8">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Minuten pro Athlet */}
                <div className="space-y-3">
                  <label className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <i className="fas fa-clock text-slate-500 dark:text-slate-400 mr-3"></i>
                    {t("admin.settings.minutesPerAthlete", "Minuten pro Athlet")}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={settings.minutes_per_athlete?.value || '2'}
                    onChange={(e) => handleSettingChange('minutes_per_athlete', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {settings.minutes_per_athlete?.description || 'Minuten pro Athlet für die Zeitplanberechnung'}
                  </p>
                </div>

                {/* Pufferzeit zwischen Gruppen */}
                <div className="space-y-3">
                  <label className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <i className="fas fa-pause text-slate-500 dark:text-slate-400 mr-3"></i>
                    {t("admin.settings.bufferMinutes", "Pufferzeit zwischen Gruppen (Minuten)")}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={settings.buffer_minutes?.value || '3'}
                    onChange={(e) => handleSettingChange('buffer_minutes', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {settings.buffer_minutes?.description || 'Pufferzeit in Minuten zwischen Gruppen'}
                  </p>
                </div>

                {/* Tägliche Startzeit */}
                <div className="space-y-3">
                  <label className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <i className="fas fa-sun text-slate-500 dark:text-slate-400 mr-3"></i>
                    {t("admin.settings.dailyStartTime", "Tägliche Startzeit")}
                  </label>
                  <input
                    type="time"
                    value={settings.daily_start_time?.value?.substring(0, 5) || '09:00'}
                    onChange={(e) => handleSettingChange('daily_start_time', e.target.value + ':00')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {settings.daily_start_time?.description || 'Startzeit pro Tag (HH:MM:SS)'}
                  </p>
                </div>

                {/* Tägliche Endzeit */}
                <div className="space-y-3">
                  <label className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <i className="fas fa-moon text-slate-500 dark:text-slate-400 mr-3"></i>
                    {t("admin.settings.dailyEndTime", "Tägliche Endzeit")}
                  </label>
                  <input
                    type="time"
                    value={settings.daily_end_time?.value?.substring(0, 5) || '17:00'}
                    onChange={(e) => handleSettingChange('daily_end_time', e.target.value + ':00')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {settings.daily_end_time?.description || 'Endzeit pro Tag, ab wann zum nächsten Tag gewechselt wird (HH:MM:SS)'}
                  </p>
                </div>

              {/* Startzeit Mittagspause */}
              <div className="space-y-3">
                <label className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                  <i className="fas fa-utensils text-slate-500 dark:text-slate-400 mr-3"></i>
                  {t("admin.settings.lunchStartTime", "Startzeit Mittagspause")}
                </label>
                <input
                  type="time"
                  value={settings.lunch_start_time?.value?.substring(0, 5) || '12:30'}
                  onChange={(e) => handleSettingChange('lunch_start_time', e.target.value + ':00')}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {settings.lunch_start_time?.description || 'Uhrzeit, zu der die tägliche Mittagspause beginnt (HH:MM:SS)'} 
                </p>
              </div>

              {/* Dauer Mittagspause */}
              <div className="space-y-3">
                <label className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                  <i className="fas fa-hourglass-half text-slate-500 dark:text-slate-400 mr-3"></i>
                  {t("admin.settings.lunchDuration", "Dauer Mittagspause (Minuten)")}
                </label>
                <input
                  type="number"
                  min="0"
                  max="180"
                  value={settings.lunch_duration_minutes?.value || '60'}
                  onChange={(e) => handleSettingChange('lunch_duration_minutes', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {settings.lunch_duration_minutes?.description || 'Dauer der Mittagspause in Minuten. Wird bei der Zeitplangenerierung berücksichtigt.'}
                </p>
              </div>

                {/* Wettkampf-Startdatum */}
                <div className="space-y-3">
                  <label className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <i className="fas fa-calendar-plus text-slate-500 dark:text-slate-400 mr-3"></i>
                    {t("admin.settings.competitionStartDate", "Wettkampf-Startdatum")}
                  </label>
                  <input
                    type="date"
                    value={settings.competition_start_date?.value || '2026-07-15'}
                    onChange={(e) => handleSettingChange('competition_start_date', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {settings.competition_start_date?.description || 'Startdatum des Wettkampfs (YYYY-MM-DD)'}
                  </p>
                </div>

                {/* Wettkampf-Enddatum */}
                <div className="space-y-3">
                  <label className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <i className="fas fa-calendar-check text-slate-500 dark:text-slate-400 mr-3"></i>
                    {t("admin.settings.competitionEndDate", "Wettkampf-Enddatum")}
                  </label>
                  <input
                    type="date"
                    value={settings.competition_end_date?.value || '2026-07-17'}
                    onChange={(e) => handleSettingChange('competition_end_date', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {settings.competition_end_date?.description || 'Enddatum des Wettkampfs (YYYY-MM-DD)'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Benutzer – Datenmanagement */}
        {activeView !== 'org' && (
        <section className="mt-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-trash text-red-600 dark:text-red-400"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">Ausgewählte Daten zurücksetzen</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">Wählen Sie aus, welche Daten gelöscht werden sollen. Abhängigkeiten werden automatisch berücksichtigt.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 text-sm">
                  <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <input type="checkbox" className="rounded border-slate-300 dark:border-slate-600" checked={deleteOptions.results} onChange={(e)=>setDeleteOptions(v=>({...v, results: e.target.checked}))} />
                    Ergebnisse
                  </label>
                  <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <input type="checkbox" className="rounded border-slate-300 dark:border-slate-600" checked={deleteOptions.schedule} onChange={(e)=>setDeleteOptions(v=>({...v, schedule: e.target.checked}))} />
                    Zeitplan-Events
                  </label>
                  <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <input type="checkbox" className="rounded border-slate-300 dark:border-slate-600" checked={deleteOptions.participantGroups} onChange={(e)=>setDeleteOptions(v=>({...v, participantGroups: e.target.checked}))} />
                    Teilnehmer-Gruppen
                  </label>
                  <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <input type="checkbox" className="rounded border-slate-300 dark:border-slate-600" checked={deleteOptions.participantCategories} onChange={(e)=>setDeleteOptions(v=>({...v, participantCategories: e.target.checked}))} />
                    Teilnehmer-Kategorien
                  </label>
                  <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <input type="checkbox" className="rounded border-slate-300 dark:border-slate-600" checked={deleteOptions.groups} onChange={(e)=>setDeleteOptions(v=>({...v, groups: e.target.checked}))} />
                    Gruppen
                  </label>
                  <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <input type="checkbox" className="rounded border-slate-300 dark:border-slate-600" checked={deleteOptions.participants} onChange={(e)=>setDeleteOptions(v=>({...v, participants: e.target.checked}))} />
                    Teilnehmer
                  </label>
                  <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <input type="checkbox" className="rounded border-slate-300 dark:border-slate-600" checked={deleteOptions.teamData} onChange={(e)=>setDeleteOptions(v=>({...v, teamData: e.target.checked}))} />
                    Team-Wettbewerb Daten
                  </label>
                </div>
                <button
                  onClick={handleResetData}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 bg-white dark:bg-slate-800 text-red-700 dark:text-red-400 text-sm font-medium rounded-lg border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500/20 disabled:opacity-50 transition-colors"
                >
                  <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-trash'} mr-2`}></i>
                  {saving ? 'Arbeite...' : 'Daten zurücksetzen'}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-seedling text-blue-600 dark:text-blue-400"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">Demo-Daten generieren</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">Erstellt Beispiel-Teilnehmer. Anzahl und Verteilungen sind konfigurierbar.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 text-sm">
                  <label className="flex items-center justify-between gap-2 text-slate-700 dark:text-slate-300">
                    <span>Gesamtanzahl</span>
                    <input type="number" min={100} max={1000} value={demoOptions.totalParticipants}
                      onChange={(e)=> setDemoOptions(v=>({ ...v, totalParticipants: Math.max(100, Math.min(1000, Number(e.target.value)||200)) }))}
                      className="w-28 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700" />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-slate-700 dark:text-slate-300">
                    <span>Männlich (%)</span>
                    <input type="number" min={0} max={100} value={demoOptions.genderRatio.M}
                      onChange={(e)=> setDemoOptions(v=>({ ...v, genderRatio: { ...v.genderRatio, M: Math.max(0, Math.min(100, Number(e.target.value)||0)), W: Math.max(0, Math.min(100, 100 - (Number(e.target.value)||0))) } }))}
                      className="w-28 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700" />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-slate-700 dark:text-slate-300">
                    <span>Weiblich (%)</span>
                    <input type="number" min={0} max={100} value={demoOptions.genderRatio.W}
                      onChange={(e)=> setDemoOptions(v=>({ ...v, genderRatio: { ...v.genderRatio, W: Math.max(0, Math.min(100, Number(e.target.value)||0)), M: Math.max(0, Math.min(100, 100 - (Number(e.target.value)||0))) } }))}
                      className="w-28 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700" />
                  </label>
                </div>
                {ageGroups.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">Altersklassen-Verteilung (%) – Summe 100</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {ageGroups
                        .filter(ag => ag.name !== 'Alle') // "Alle" ausschließen
                        .map(ag => (
                          <label key={ag.id} className="flex items-center justify-between gap-2 text-slate-700 dark:text-slate-300 text-sm">
                            <span>{ag.name}</span>
                            <input type="number" min={0} max={100} value={demoOptions.ageGroupRatios[ag.id] ?? 0}
                              onChange={(e)=> setDemoOptions(v=>({ ...v, ageGroupRatios: { ...v.ageGroupRatios, [ag.id]: Math.max(0, Math.min(100, Number(e.target.value)||0)) } }))}
                              className="w-28 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700" />
                          </label>
                        ))}
                    </div>
                  </div>
                )}
                <button
                  onClick={handleSeedDemo}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 bg-slate-900 dark:bg-slate-700 text-white dark:text-slate-100 text-sm font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 transition-colors"
                >
                  <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-magic'} mr-2`}></i>
                  {saving ? 'Arbeite...' : 'Demo-Daten erzeugen'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </section>
        )}
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${
            toast.type === 'success' ? 'border-green-200 dark:border-green-800' :
            toast.type === 'error' ? 'border-red-200 dark:border-red-800' :
            'border-blue-200 dark:border-blue-800'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                toast.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
                toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                'bg-blue-100 dark:bg-blue-900/30'
              }`}>
                <i className={`fas text-xs ${
                  toast.type === 'success' ? 'fa-check text-green-600 dark:text-green-400' :
                  toast.type === 'error' ? 'fa-times text-red-600 dark:text-red-400' :
                  'fa-info text-blue-600 dark:text-blue-400'
                }`}></i>
              </div>
              <span className={`text-sm font-medium ${
                toast.type === 'success' ? 'text-green-900 dark:text-green-100' :
                toast.type === 'error' ? 'text-red-900 dark:text-red-100' :
                'text-blue-900 dark:text-blue-100'
              }`}>{toast.message}</span>
              <button
                onClick={hideToast}
                className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bestätigungs-Dialog für Datenaktionen */}
      {showActionConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full border border-slate-200 dark:border-slate-700">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  actionType === 'reset-data' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <i className={`fas text-xl ${
                    actionType === 'reset-data'
                      ? 'fa-exclamation-triangle text-red-600 dark:text-red-400'
                      : 'fa-seedling text-blue-600 dark:text-blue-400'
                  }`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {actionType === 'reset-data' ? 'Ausgewählte Daten zurücksetzen' : 'Demo-Daten generieren'}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {actionType === 'reset-data'
                      ? 'Diese Aktion kann nicht rückgängig gemacht werden.'
                      : 'Es werden Beispiel-Daten für Demo-Zwecke angelegt.'}
                  </p>
                </div>
              </div>
              <p className="text-slate-700 dark:text-slate-300 mb-6">
                {actionType === 'reset-data'
                  ? 'Möchten Sie die ausgewählten Daten wirklich löschen? Abhängigkeiten werden berücksichtigt.'
                  : 'Möchten Sie Demo-Daten erzeugen? Vorhandene dynamische Daten könnten überschrieben werden.'}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowActionConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={async () => {
                    setShowActionConfirm(false);
                    if (actionType === 'reset-data') {
                      await performResetData();
                    } else {
                      await performSeedDemo();
                    }
                  }}
                  disabled={saving}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                    actionType === 'reset-data'
                      ? 'bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-600'
                      : 'bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600'
                  }`}
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      {actionType === 'reset-data' ? 'Lösche...' : 'Erzeuge...'}
                    </>
                  ) : (
                    <>
                      {actionType === 'reset-data' ? 'Daten löschen' : 'Demo-Daten erzeugen'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmGenerate}
        title="Zeiten im Zeitplan aktualisieren"
        message="Die Einstellungen wurden geändert. Möchten Sie die Zeiten im bestehenden Zeitplan basierend auf den neuen Einstellungen aktualisieren? Die Reihenfolge und Flächenzuordnung der Events bleibt erhalten."
        confirmText="Zeiten aktualisieren"
        cancelText="Abbrechen"
        type="info"
      />
    </main>
  );
};

export default SettingsTailwind;
